import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useToast } from "../ui/use-toast";

// Common timezones list
const commonTimezones = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "America/Honolulu",
  "America/Puerto_Rico",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney",
  "Pacific/Auckland"
];

export function TimezoneSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchTimezone = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("timezone")
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Error fetching timezone:", error);
          return;
        }

        if (data?.timezone) {
          setSelectedTimezone(data.timezone);
        }
      } catch (error) {
        console.error("Error in fetchTimezone:", error);
      }
    };

    fetchTimezone();
  }, [user?.id]);

  const handleTimezoneChange = async (newTimezone: string) => {
    if (!user?.id) return;
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ timezone: newTimezone })
        .eq("id", user.id);

      if (error) throw error;

      setSelectedTimezone(newTimezone);
      toast({
        title: "Timezone updated",
        description: "Your timezone preference has been saved.",
      });
    } catch (error: any) {
      console.error("Error updating timezone:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update timezone. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 mt-6 pb-6 border-b">
      <div>
        <h3 className="text-lg font-semibold mb-2">Timezone Settings</h3>
        <p className="text-muted-foreground">
          Choose your preferred timezone for displaying dates and times.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="timezone">Timezone</Label>
        <Select
          value={selectedTimezone}
          onValueChange={handleTimezoneChange}
          disabled={isLoading}
        >
          <SelectTrigger className="w-full md:w-[300px]">
            <SelectValue placeholder="Select timezone" />
          </SelectTrigger>
          <SelectContent>
            {commonTimezones.map((timezone) => (
              <SelectItem key={timezone} value={timezone}>
                {timezone.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}